import { useState } from 'react';
import { GPS_COMMAND_TYPES } from '@/types/gps';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Send, Power, PowerOff, RotateCw, MapPin, Settings } from 'lucide-react';

interface CommandPanelProps {
  deviceId: string;
  onSendCommand: (commandType: string, payload?: Record<string, any>) => void;
  isLoading?: boolean;
}

export function CommandPanel({ deviceId, onSendCommand, isLoading }: CommandPanelProps) {
  const [commandType, setCommandType] = useState<string>(GPS_COMMAND_TYPES.POSITION_SINGLE);
  const [customPayload, setCustomPayload] = useState('{}');

  const quickCommands = [
    {
      type: GPS_COMMAND_TYPES.POSITION_SINGLE,
      label: 'Solicitar Posição',
      icon: MapPin,
      description: 'Solicita a posição atual do dispositivo',
    },
    {
      type: GPS_COMMAND_TYPES.ENGINE_STOP,
      label: 'Desligar Motor',
      icon: PowerOff,
      description: 'Bloqueia o motor do veículo',
      variant: 'destructive' as const,
    },
    {
      type: GPS_COMMAND_TYPES.ENGINE_RESUME,
      label: 'Religar Motor',
      icon: Power,
      description: 'Desbloqueia o motor do veículo',
    },
    {
      type: GPS_COMMAND_TYPES.REBOOT,
      label: 'Reiniciar',
      icon: RotateCw,
      description: 'Reinicia o dispositivo GPS',
    },
  ];

  const handleQuickCommand = (type: string) => {
    onSendCommand(type);
  };

  const handleCustomCommand = () => {
    try {
      const payload = JSON.parse(customPayload);
      onSendCommand(commandType, payload);
    } catch (error) {
      alert('JSON inválido no payload');
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Commands */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Comandos Rápidos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {quickCommands.map((cmd) => {
            const Icon = cmd.icon;
            return (
              <Card key={cmd.type} className="p-4">
                <Button
                  variant={cmd.variant || 'outline'}
                  className="w-full mb-2"
                  onClick={() => handleQuickCommand(cmd.type)}
                  disabled={isLoading}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {cmd.label}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  {cmd.description}
                </p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Custom Command */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Comando Personalizado
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="command-type">Tipo de Comando</Label>
            <Select value={commandType} onValueChange={setCommandType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={GPS_COMMAND_TYPES.ENGINE_STOP}>Desligar Motor</SelectItem>
                <SelectItem value={GPS_COMMAND_TYPES.ENGINE_RESUME}>Religar Motor</SelectItem>
                <SelectItem value={GPS_COMMAND_TYPES.POSITION_SINGLE}>Solicitar Posição</SelectItem>
                <SelectItem value={GPS_COMMAND_TYPES.REBOOT}>Reiniciar</SelectItem>
                <SelectItem value={GPS_COMMAND_TYPES.SET_UPDATE_INTERVAL}>
                  Configurar Intervalo
                </SelectItem>
                <SelectItem value={GPS_COMMAND_TYPES.CUSTOM}>Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payload">Payload (JSON)</Label>
            <Textarea
              id="payload"
              value={customPayload}
              onChange={(e) => setCustomPayload(e.target.value)}
              placeholder='{"interval": 30, "unit": "seconds"}'
              rows={4}
              className="font-mono text-sm"
            />
          </div>

          <Button
            onClick={handleCustomCommand}
            disabled={isLoading}
            className="w-full"
          >
            <Send className="h-4 w-4 mr-2" />
            Enviar Comando
          </Button>
        </div>
      </Card>
    </div>
  );
}
